import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, PlayCircle, Lightbulb, CheckCircle, Clock, Calendar, Share2, Image, BarChart3, Edit3, CalendarCheck, Bot, BookOpen, Info, Home, ArrowRight, Settings } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'overview', title: 'Overview' },
  { id: 'supported-platforms', title: 'Supported Platforms' },
  { id: 'video-tutorial', title: 'Video Tutorial' },
  { id: 'creating-posts', title: 'Creating Posts' },
  { id: 'scheduling', title: 'Scheduling Posts' },
  { id: 'content-calendar', title: 'Content Calendar' },
  { id: 'analytics', title: 'Analytics' },
  { id: 'best-practices', title: 'Best Practices' },
];

const relatedArticles = [
  { title: 'Getting Started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Brain AI Tutorial', href: '/doc/article/brain-ai-tutorial' },
  { title: 'AI Models in SweetPilot', href: '/doc/article/ai-models' },
  { title: 'Onboarding Tutorial', href: '/doc/article/onboarding-tutorial' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
];

const supportedPlatforms = [
  { name: 'Instagram', color: 'text-pink-500', bgColor: 'bg-pink-100', features: 'Posts, Stories, Reels' },
  { name: 'Twitter/X', color: 'text-blue-400', bgColor: 'bg-blue-100', features: 'Tweets, Threads' },
  { name: 'Facebook', color: 'text-blue-600', bgColor: 'bg-blue-100', features: 'Posts, Stories, Pages' },
  { name: 'LinkedIn', color: 'text-blue-700', bgColor: 'bg-blue-100', features: 'Posts, Articles' }
];

const keySteps = [
  {
    icon: Edit3,
    title: 'Create Your Post',
    description: 'Write your content, add media, and customize for each platform',
    duration: '3 min'
  },
  {
    icon: Image,
    title: 'Add Media',
    description: 'Upload images, videos, or use AI-generated visuals',
    duration: '2 min'
  },
  {
    icon: Calendar,
    title: 'Schedule Date & Time',
    description: 'Choose the optimal time for your audience',
    duration: '2 min'
  },
  {
    icon: Share2,
    title: 'Select Platforms',
    description: 'Choose which social networks to publish to',
    duration: '1 min'
  },
  {
    icon: Bot,
    title: 'AI Optimization',
    description: 'Let AI suggest hashtags, captions, and best posting times',
    duration: '3 min'
  },
  {
    icon: BarChart3,
    title: 'Track Performance',
    description: 'Monitor engagement and analytics after publishing',
    duration: '2 min'
  }
];

interface TipBoxProps {
  type: 'tip' | 'warning' | 'info';
  title: string;
  children: React.ReactNode;
}

const TipBox = ({ type, title, children }: TipBoxProps) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const styles = {
    tip: {
      bg: isDark ? 'bg-green-900/30' : 'bg-green-50',
      border: 'border-green-500',
      icon: <Lightbulb className="w-5 h-5 text-green-500" />,
      titleColor: isDark ? 'text-green-400' : 'text-green-700'
    },
    warning: {
      bg: isDark ? 'bg-yellow-900/30' : 'bg-yellow-50',
      border: 'border-yellow-500',
      icon: <Info className="w-5 h-5 text-yellow-500" />,
      titleColor: isDark ? 'text-yellow-400' : 'text-yellow-700'
    },
    info: {
      bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50',
      border: 'border-blue-500',
      icon: <Info className="w-5 h-5 text-blue-500" />,
      titleColor: isDark ? 'text-blue-400' : 'text-blue-700'
    }
  };

  const style = styles[type];

  return (
    <div className={`${style.bg} border-l-4 ${style.border} p-4 rounded-r-lg my-4`}>
      <div className="flex items-center gap-2 mb-2">
        {style.icon}
        <span className={`font-semibold ${style.titleColor}`}>{title}</span>
      </div>
      <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
        {children}
      </div>
    </div>
  );
};

const SocialMediaManagerTutorial = () => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');

  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'Schedule Posts with SweetPilot Social Media Manager - Complete Video Tutorial',
    description: 'Learn how to schedule and manage social media posts across multiple platforms using SweetPilot Social Media Manager. Step-by-step video tutorial with best practices.',
    thumbnailUrl: 'https://sweetpilot.com/thumbnails/social-media-manager-tutorial.jpg',
    uploadDate: '2024-01-20',
    duration: 'PT18M',
    contentUrl: 'https://sweetpilot.com/videos/social-media-manager-tutorial.mp4',
    embedUrl: 'https://sweetpilot.com/embed/social-media-manager-tutorial',
    publisher: {
      '@type': 'Organization',
      name: 'SweetPilot',
      logo: {
        '@type': 'ImageObject',
        url: 'https://sweetpilot.com/logo.png'
      }
    },
    author: {
      '@type': 'Organization',
      name: 'SweetPilot Team'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://sweetpilot.com/doc/article/social-media-manager-tutorial'
    },
    datePublished: '2024-01-20',
    dateModified: '2024-12-01'
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
      <Helmet>
        <title>Schedule Posts with SweetPilot Social Media Manager | Video Tutorial</title>
        <meta name="description" content="Learn how to schedule and manage social media posts across Instagram, Twitter, Facebook, and LinkedIn using SweetPilot Social Media Manager. Complete video tutorial." />
        <meta name="keywords" content="SweetPilot social media manager, schedule posts, social media scheduling, Instagram scheduling, Twitter scheduling, Facebook posts, LinkedIn posts, social media automation, content calendar" />
        <meta property="og:title" content="Schedule Posts with SweetPilot Social Media Manager - Video Tutorial" />
        <meta property="og:description" content="Master social media scheduling with SweetPilot. Learn to create, schedule, and analyze posts across all major platforms." />
        <meta property="og:type" content="video.other" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/social-media-manager-tutorial" />
        <meta property="og:image" content="https://sweetpilot.com/thumbnails/social-media-manager-tutorial.jpg" />
        <meta property="og:video" content="https://sweetpilot.com/videos/social-media-manager-tutorial.mp4" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Schedule Posts with SweetPilot Social Media Manager" />
        <meta name="twitter:description" content="Complete video tutorial on scheduling and managing social media posts with SweetPilot." />
        <meta name="twitter:image" content="https://sweetpilot.com/thumbnails/social-media-manager-tutorial.jpg" />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/social-media-manager-tutorial" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className={`${isDark ? 'bg-gradient-to-r from-pink-900 via-purple-900 to-indigo-900' : 'bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500'} text-white py-16`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm mb-6 text-pink-200">
              <Link to="/" className="hover:text-white flex items-center gap-1">
                <Home className="w-4 h-4" /> Home
              </Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/help" className="hover:text-white">Help Center</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Social Media Manager Tutorial</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-10 h-10 text-pink-300" />
                <span className="px-3 py-1 bg-pink-500/30 rounded-full text-sm">Video Tutorial</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Schedule Posts with Social Media Manager
              </h1>
              <p className="text-xl text-pink-200 max-w-3xl">
                Master the art of social media scheduling. Learn how to create, schedule, and manage posts across all major platforms from one unified dashboard.
              </p>
              <div className="flex items-center gap-4 mt-6 text-pink-200">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> 18 min video
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> 6 key steps
                </span>
              </div>

              {/* Platform Icons */}
              <div className="flex items-center gap-4 mt-6">
                <span className="px-3 py-1 bg-pink-500/30 rounded-full text-sm">Instagram</span>
                <span className="px-3 py-1 bg-blue-500/30 rounded-full text-sm">Twitter</span>
                <span className="px-3 py-1 bg-blue-600/30 rounded-full text-sm">Facebook</span>
                <span className="px-3 py-1 bg-blue-700/30 rounded-full text-sm">LinkedIn</span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Table of Contents */}
            <div className="lg:col-span-1">
              <div className={`sticky top-24 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        activeSection === item.id
                          ? isDark
                            ? 'bg-pink-600 text-white'
                            : 'bg-pink-100 text-pink-700'
                          : isDark
                          ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-8`}>
                {/* Overview Section */}
                <section id="overview" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Share2 className="w-6 h-6 text-pink-500" /> Overview
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    SweetPilot Social Media Manager is your all-in-one solution for managing social media content across multiple platforms. Say goodbye to logging into multiple accounts and juggling different apps—manage everything from a single, intuitive dashboard.
                  </p>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    With AI-powered suggestions, optimal timing recommendations, and comprehensive analytics, you'll save hours every week while improving your social media performance.
                  </p>

                  <TipBox type="info" title="What You'll Learn">
                    This tutorial covers creating posts, scheduling content, using the content calendar, leveraging AI for optimization, and analyzing your performance—all in under 20 minutes.
                  </TipBox>
                </section>

                {/* Supported Platforms Section */}
                <section id="supported-platforms" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Share2 className="w-6 h-6 text-pink-500" /> Supported Platforms
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {supportedPlatforms.map((platform, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-4 rounded-lg border ${
                          isDark
                            ? 'bg-gray-700/50 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-gray-600' : platform.bgColor} flex items-center justify-center`}>
                            <Share2 className={`w-5 h-5 ${platform.color}`} />
                          </div>
                          <div>
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {platform.name}
                            </h3>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {platform.features}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <TipBox type="tip" title="Pro Tip">
                    Connect all your social accounts during initial setup to unlock cross-platform posting and unified analytics.
                  </TipBox>
                </section>

                {/* Video Tutorial Section */}
                <section id="video-tutorial" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <PlayCircle className="w-6 h-6 text-pink-500" /> Video Tutorial
                  </h2>
                  <div className={`aspect-video rounded-xl overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center`}>
                    <div className="text-center">
                      <div className={`w-20 h-20 rounded-full ${isDark ? 'bg-pink-600' : 'bg-pink-500'} flex items-center justify-center mx-auto mb-4 cursor-pointer hover:scale-110 transition-transform`}>
                        <PlayCircle className="w-10 h-10 text-white" />
                      </div>
                      <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Click to play the Social Media Manager tutorial
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-2`}>
                        Duration: 18 minutes
                      </p>
                    </div>
                  </div>
                </section>

                {/* Creating Posts Section */}
                <section id="creating-posts" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Edit3 className="w-6 h-6 text-pink-500" /> Creating Posts
                  </h2>
                  <div className="space-y-4">
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Creating posts in SweetPilot is designed to be intuitive while offering powerful customization options:
                    </p>

                    <div className={`p-4 rounded-lg border-l-4 border-pink-500 ${isDark ? 'bg-gray-700/50' : 'bg-pink-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        1. Start with Content
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Write your post content in the main editor. You can write once and customize for each platform, or create platform-specific versions.
                      </p>
                    </div>

                    <div className={`p-4 rounded-lg border-l-4 border-pink-500 ${isDark ? 'bg-gray-700/50' : 'bg-pink-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        2. Add Media
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Upload images or videos, or use our AI image generator to create custom visuals. The system automatically optimizes media for each platform's requirements.
                      </p>
                    </div>

                    <div className={`p-4 rounded-lg border-l-4 border-pink-500 ${isDark ? 'bg-gray-700/50' : 'bg-pink-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        3. AI Assistance
                      </h3>
                      <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Use the AI assistant to generate hashtags, improve your caption, or get suggestions for better engagement. Click the magic wand icon to access AI features.
                      </p>
                    </div>
                  </div>

                  <TipBox type="tip" title="Character Limits">
                    Each platform has different character limits. SweetPilot shows real-time character counts and warns you when approaching limits for each selected platform.
                  </TipBox>
                </section>

                {/* Scheduling Section */}
                <section id="scheduling" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Calendar className="w-6 h-6 text-pink-500" /> Scheduling Posts
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {keySteps.map((step, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-4 rounded-lg border ${
                          isDark
                            ? 'bg-gray-700/50 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-pink-600' : 'bg-pink-100'}`}>
                            <step.icon className={`w-5 h-5 ${isDark ? 'text-white' : 'text-pink-600'}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {step.title}
                            </h3>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {step.description}
                            </p>
                            <span className={`inline-flex items-center gap-1 text-xs mt-2 ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>
                              <Clock className="w-3 h-3" /> {step.duration}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <TipBox type="info" title="Optimal Timing">
                    SweetPilot analyzes your audience's activity patterns and suggests the best times to post for maximum engagement. Look for the "Best Time" badge when scheduling.
                  </TipBox>
                </section>

                {/* Content Calendar Section */}
                <section id="content-calendar" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <CalendarCheck className="w-6 h-6 text-pink-500" /> Content Calendar
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    The content calendar provides a visual overview of all your scheduled and published content:
                  </p>

                  <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'} mb-4`}>
                    <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Calendar Features:
                    </h4>
                    <ul className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-500" /> Monthly, weekly, and daily views
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-500" /> Drag and drop to reschedule posts
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-500" /> Color-coded by platform
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-500" /> Filter by status (scheduled, published, draft)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-pink-500" /> Quick edit and preview on hover
                      </li>
                    </ul>
                  </div>

                  <TipBox type="warning" title="Time Zones">
                    The calendar displays times in your local timezone. When collaborating with team members in different locations, ensure everyone is aware of the timezone settings.
                  </TipBox>
                </section>

                {/* Analytics Section */}
                <section id="analytics" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <BarChart3 className="w-6 h-6 text-pink-500" /> Analytics & Insights
                  </h2>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Track your social media performance with comprehensive analytics:
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-gray-700/50' : 'bg-pink-50'}`}>
                      <h4 className={`text-2xl font-bold ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>Engagement</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Likes, comments, shares</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <h4 className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Reach</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Impressions, audience growth</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-gray-700/50' : 'bg-indigo-50'}`}>
                      <h4 className={`text-2xl font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Conversions</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Clicks, leads, sales</p>
                    </div>
                  </div>

                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Use these insights to refine your content strategy and understand what resonates with your audience.
                  </p>
                </section>

                {/* Best Practices Section */}
                <section id="best-practices" className="mb-12">
                  <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Lightbulb className="w-6 h-6 text-pink-500" /> Best Practices
                  </h2>
                  <div className="space-y-4">
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold">1</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Plan Ahead</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Schedule content at least a week in advance. Use batch creation sessions to create multiple posts efficiently.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold">2</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Customize for Each Platform</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          While cross-posting saves time, tailoring content for each platform's audience and format yields better results.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold">3</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Use AI Suggestions</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Let AI help with hashtags, captions, and timing. The suggestions are based on current trends and your audience data.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold">4</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Monitor and Adjust</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Regularly review your analytics. Identify top-performing content and replicate what works.
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold">5</span>
                      <div>
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Engage with Your Audience</h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Scheduling posts is just the start. Make time to respond to comments and messages to build community.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Related Articles */}
                <section className={`border-t pt-8 mt-8 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Related Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link
                        key={index}
                        to={article.href}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                          isDark
                            ? 'border-gray-700 hover:bg-gray-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <BookOpen className={`w-5 h-5 ${isDark ? 'text-pink-400' : 'text-pink-600'}`} />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{article.title}</span>
                        <ArrowRight className={`w-4 h-4 ml-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      </Link>
                    ))}
                  </div>
                </section>

                {/* Feedback Section */}
                <section className={`mt-8 p-6 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Was this tutorial helpful?
                  </h3>
                  <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Help us improve by sharing your feedback
                  </p>
                  <div className="flex gap-4">
                    <button className={`px-6 py-2 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}>
                      Yes, helpful
                    </button>
                    <button className={`px-6 py-2 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-gray-600 hover:bg-gray-500 text-white'
                        : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                    }`}>
                      Needs improvement
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};

export default SocialMediaManagerTutorial;
